import React, { createContext, useCallback, useState } from "react";
import { clientFetch } from "../utils/clientFetch";
import { ContextFetchResult } from "../types/contextFetchResult";
import i18n from "../i18n";

export const defaultUser: UserResponseType = {
  position: null,
  name: '',
  email: '',
  start_date: '',
  id: '',
  pin_code: '',
  is_disabled: false,
  is_admin: false,
  created_at: '',
  last_login_at: '',
  department: {
    created_at: '',
    id: 0,
    name: '',
    updated_at: ''
  }
}

export type UserRequestType = {
  departmentId: number
  position?: string | null
  name: string
  email: string
  start_date: string
  pin_code?: string
  is_admin?: boolean
}

export type UserResponseType = {
  position: string | null,
  name: string,
  email: string,
  start_date: string,
  id: string,
  pin_code?: string,
  is_disabled?: boolean
  is_admin?: boolean
  created_at: string,
  last_login_at: string,
  department: {
    created_at: string
    id: number
    name: string
    updated_at: string
  }
}

export type DepartmentUsersResponse = {
  data: UserResponseType[]
  meta: {
    total: number
    active: number
    page: number
    limit: number
  }
}

export const UsersContext = createContext<{
  user: UserResponseType
}>({
  user: defaultUser,
})

export const UpdateUsersContext = createContext<{
  registerUser: (value: UserRequestType) => Promise<(string | boolean)[]>
  refreshUser: (id: string) => Promise<ContextFetchResult<UserResponseType>>
  updateUser: (value: UserRequestType, id: string) => Promise<(string | boolean)[]>
  toggleUserDisabled: (id: string) => Promise<void>
  reissueUserPinCode: (id: string) => Promise<boolean>
  reissueUserPassword: (id: string) => Promise<boolean>
  refreshDepartmentUsers: (departmentId: string, page?: number, limit?: number, sortField?: string, sortOrder?: string) => Promise<DepartmentUsersResponse>
}>({
  registerUser: async (value: UserRequestType) => [],
  refreshUser: async (_id: string) => ({ status: null, data: null }),
  updateUser: async (value: UserRequestType, id: string) => [],
  toggleUserDisabled: async (id: string) => { },
  reissueUserPinCode: async (id: string) => false,
  reissueUserPassword: async (id: string) => false,
  refreshDepartmentUsers: async (departmentId: string, page?: number, limit?: number, sortField?: string, sortOrder?: string) => ({
    data: [],
    meta: {
      total: 0,
      active: 0,
      page: page ?? 1,
      limit: limit ?? 10
    }
  })
})

export function useUsersContext() {
  const [user, setUser] = useState<UserResponseType>(defaultUser)
  // ユーザ登録
  const registerUser = useCallback(async (value: UserRequestType) => {
    try {
      const res = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(value),
      })
      if (res.status === 409) {
        return [i18n.t("departmentUser.messages.emailAlreadyRegistered"), false]
      }

      if (res.status === 401) {
        return [i18n.t("departmentUser.messages.authenticationFailed"), false]
      }

      if (!res.ok) console.log('error')

      const data = await res.json();
      setUser(data);
      return [i18n.t("departmentUser.messages.registerSucceeded"), true]
    } catch (error) {
      console.error('Error fetch')
      return [i18n.t("departmentUser.messages.genericError"), false]
    }
  }, [])

  // ユーザIDでユーザ詳細取得
  const refreshUser = useCallback(async (id: string): Promise<ContextFetchResult<UserResponseType>> => {
    try {
      const res = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/users/${id}`, {
        headers: { 'Content-Type': 'application/json' },
      })
      if (res.status === 401) {
        return {
          status: res.status,
          data: null
        }
      }

      if (!res.ok) {
        console.log('error')
        return {
          status: res.status,
          data: null
        }
      }

      const data = await res.json()
      setUser(data)
      return {
        status: res.status,
        data
      }
    } catch (error) {
      console.error('Error fetch', error)
      return {
        status: null,
        data: null
      }
    }
  }, [])

  // 部署IDでユーザ一覧取得
  const refreshDepartmentUsers = useCallback(async (departmentId: string, page = 1, limit = 10, sortField?: string, sortOrder?: string): Promise<DepartmentUsersResponse> => {
    const safePage = page < 1 ? 1 : page
    const safeLimit = limit < 1 ? 10 : limit

    if (!departmentId) {
      return {
        data: [],
        meta: {
          total: 0,
          active: 0,
          page: safePage,
          limit: safeLimit
        }
      }
    }

    try {
      const searchParams = new URLSearchParams({
        page: String(safePage),
        limit: String(safeLimit)
      })
      if (sortField && sortOrder) {
        searchParams.append('sortField', sortField);
        searchParams.append('sortOrder', sortOrder);
      }

      const res = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/users/department/${departmentId}?${searchParams.toString()}`, {
        headers: { 'Content-Type': 'application/json' },
      })
      if (res.status === 401) {
        return {
          data: [],
          meta: {
            total: 0,
            active: 0,
            page: safePage,
            limit: safeLimit
          }
        }
      }

      if (!res.ok) {
        console.log('error')
        return {
          data: [],
          meta: {
            total: 0,
            active: 0,
            page: safePage,
            limit: safeLimit
          }
        }
      }

      const data = await res.json()

      if (Array.isArray(data)) {
        const activeCount = data.filter((user: UserResponseType) => user.is_disabled === false).length
        return {
          data,
          meta: {
            total: data.length,
            active: activeCount,
            page: safePage,
            limit: safeLimit
          }
        }
      }

      const users = Array.isArray(data?.data) ? data.data as UserResponseType[] : []
      const meta = typeof data?.meta === 'object' && data.meta !== null
        ? data.meta as Partial<DepartmentUsersResponse['meta']>
        : {}

      const total = typeof meta.total === 'number' ? meta.total : users.length
      const active = typeof meta.active === 'number' ? meta.active : users.filter((user) => user.is_disabled === false).length
      const currentPage = typeof meta.page === 'number' ? meta.page : safePage
      const currentLimit = typeof meta.limit === 'number' ? meta.limit : safeLimit

      return {
        data: users,
        meta: {
          total,
          active,
          page: currentPage,
          limit: currentLimit
        }
      }
    } catch (error) {
      console.error('Error fetch', error)
      return {
        data: [],
        meta: {
          total: 0,
          active: 0,
          page: safePage,
          limit: safeLimit
        }
      }
    }
  }, [])
  // ユーザIDでユーザ情報更新
  const updateUser = useCallback(async (value: UserRequestType, id: string) => {
    try {
      const res = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/users/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(value),
      })
      if (res.status === 409) {
        return [i18n.t("departmentUser.messages.emailAlreadyRegistered"), false]
      }

      if (res.status === 401) {
        return [i18n.t("departmentUser.messages.authenticationFailed"), false]
      }

      if (!res.ok) console.log('error')

      await refreshUser(id)
      return [i18n.t("departmentUser.messages.updateSucceeded"), true]
    } catch (error) {
      console.error('Error fetch')
      return [i18n.t("departmentUser.messages.genericError"), false]
    }
  }, [refreshUser])

  // ユーザIDでユーザの状態(停止)を変更
  const toggleUserDisabled = useCallback(async (id: string) => {
    try {
      const res = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/users/${id}/toggle-disable`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
      })
      if (res.status === 401) {
        return
      }

      if (!res.ok) console.log('error')

      refreshUser(id)
    } catch (error) {
      console.error('Error fetch')
    }
  }, [refreshUser])

  // ユーザIDでユーザのPINコードを再発行
  const reissueUserPinCode = useCallback(async (id: string) => {
    try {
      const res = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/users/${id}/regenerate-pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      })
      if (res.status === 401) {
        return false
      }

      if (!res.ok) {
        console.log('error')
        return false
      }

      refreshUser(id)
      return true
    } catch (error) {
      console.error('Error fetch')
      return false
    }
  }, [refreshUser])

  // ユーザIDでユーザのパスワードを再発行
  const reissueUserPassword = useCallback(async (id: string) => {
    try {
      const res = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/users/${id}/regenerate-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      })
      if (res.status === 401) {
        return false
      }

      if (!res.ok) {
        console.log('error')
        return false
      }

      refreshUser(id)
      return true
    } catch (error) {
      console.error('Error fetch')
      return false
    }
  }, [refreshUser])

  return {
    user,
    registerUser,
    refreshUser,
    updateUser,
    toggleUserDisabled,
    reissueUserPinCode,
    reissueUserPassword,
    refreshDepartmentUsers,
  }
}

export function UsersProvider({ children }: { children: React.ReactNode }) {
  const { user, registerUser, refreshUser, updateUser, toggleUserDisabled, reissueUserPinCode, reissueUserPassword, refreshDepartmentUsers } = useUsersContext()

  return (
    <UpdateUsersContext.Provider value={{ registerUser, refreshUser, updateUser, toggleUserDisabled, reissueUserPinCode, reissueUserPassword, refreshDepartmentUsers }}>
      <UsersContext.Provider value={{ user }}>
        {children}
      </UsersContext.Provider>
    </UpdateUsersContext.Provider>
  )
}
